import { useStyles } from './styles/FormMultiSelect.styles';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import { Grid, FormControl, TextField } from '@mui/material';
import { useState, type SyntheticEvent } from 'react';

export type Option = { id: string; name: string };

interface MultiSelectProps {
    error?: boolean;
    options: Option[];
    label: string;
    selected: Option[];
    onChange: (selected: Option[]) => void;
    disable?: boolean;
    size?: number;
    loading?: boolean;
}

export const FormMultiSelect = ({
    error,
    options,
    label,
    selected,
    onChange,
    disable,
    size,
    loading = false,
}: MultiSelectProps) => {
    const classes = useStyles();
    const [open, setOpen] = useState<boolean>(false);

    const handleOpen = () => {
        if (disable === true) {
            return;
        }

        setOpen(true);
    };

    return (
        <Grid item md={size ?? 6} xs={12} className={classes.textInputWrapper}>
            <FormControl variant='outlined' className={classes.textInput} error={error} disabled={disable}>
                <Autocomplete
                    loading={loading}
                    open={open}
                    multiple
                    openOnFocus
                    disabled={disable}
                    // ? Must force the value to be an empty array
                    // https://github.com/mui/material-ui/issues/27397
                    value={loading ? [] : selected}
                    options={loading ? [] : [{ id: '*', name: 'All ' }, ...options]}
                    getOptionLabel={({ name }) => name}
                    filterSelectedOptions={true}
                    onChange={(_: SyntheticEvent<Element, Event>, values) => {
                        const [selection] = values.slice(-1);

                        const next =
                            selection?.id === '*' && !selected.some(({ id }) => id === '*')
                                ? [selection]
                                : values.filter(({ id }) => id !== '*');

                        onChange(next);
                    }}
                    onOpen={handleOpen}
                    onClose={() => {
                        setOpen(false);
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={loading ? 'Loading...' : label}
                            variant='outlined'
                            placeholder={`Select ${label}`}
                        />
                    )}
                    ChipProps={{
                        sx: {
                            pointerEvents: disable ? 'none' : 'auto',
                        },
                    }}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {option.name}
                        </li>
                    )}
                    renderTags={(selected, getTagProps) =>
                        selected.map((value, index) => (
                            <Chip
                                size='small'
                                label={value.name}
                                {...getTagProps({ index })}
                                key={index}
                                color='primary'
                                disabled={disable}
                                onClick={(e) => {
                                    if (disable) e.stopPropagation();
                                }}
                            />
                        ))
                    }
                    isOptionEqualToValue={(option, value) => option.name === value.name && option.id === value.id}
                />
            </FormControl>
        </Grid>
    );
};
